/**
 * Task #1 — Always-on client error reporter.
 *
 * Captures uncaught errors, unhandled promise rejections, failed API calls,
 * and React Error Boundary crashes, and posts them to /api/errors/report.
 *
 * Fail-safe by design:
 *  - never throws
 *  - dedupes identical errors (per signature, 60s window)
 *  - rate-limits to 10 reports/minute
 *  - never reports failures of the report endpoint itself (no loops)
 */

const REPORT_URL = "/api/errors/report";
const MAX_PER_MINUTE = 10;
const DEDUPE_WINDOW_MS = 60_000;

let lastUserAction = "";
const recentSignatures = new Map<string, number>();
let windowStart = Date.now();
let windowCount = 0;

export function setLastUserAction(action: string) {
  lastUserAction = action.slice(0, 200);
}

function allowed(signature: string): boolean {
  const now = Date.now();
  // rate limit
  if (now - windowStart > 60_000) {
    windowStart = now;
    windowCount = 0;
  }
  if (windowCount >= MAX_PER_MINUTE) return false;
  // dedupe
  const last = recentSignatures.get(signature);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recentSignatures.set(signature, now);
  if (recentSignatures.size > 100) {
    // prune oldest entries
    const cutoff = now - DEDUPE_WINDOW_MS;
    recentSignatures.forEach((t, k) => { if (t < cutoff) recentSignatures.delete(k); });
  }
  windowCount++;
  return true;
}

export interface ClientErrorReport {
  source: "client" | "client-boundary" | "client-api";
  message: string;
  stack?: string | null;
  componentStack?: string | null;
  route?: string;
  method?: string;
  statusCode?: number;
  context?: Record<string, any>;
}

export function reportError(r: ClientErrorReport): void {
  try {
    if (!r.message) return;
    // Never report failures from the report endpoint itself
    if (r.route && r.route.includes("/api/errors/report")) return;
    const signature = `${r.source}|${r.message.slice(0, 200)}|${r.route || ""}`;
    if (!allowed(signature)) return;

    const body = JSON.stringify({
      ...r,
      route: r.route || window.location.pathname,
      context: {
        ...(r.context || {}),
        lastUserAction: lastUserAction || undefined,
        href: window.location.href,
      },
    });

    fetch(REPORT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => { /* swallow — never loop */ });
  } catch {
    /* never throw */
  }
}

let installed = false;
export function installErrorReporter(): void {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    try {
      if (!event.message) return;
      // Ignore benign browser noise
      if (event.message.includes("ResizeObserver loop")) return;
      reportError({
        source: "client",
        message: event.message,
        stack: event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
      });
    } catch { /* never throw */ }
  });

  window.addEventListener("unhandledrejection", (event) => {
    try {
      const reason: any = event.reason;
      const message = reason?.message || String(reason ?? "Unhandled promise rejection");
      reportError({
        source: "client",
        message: `Unhandled rejection: ${message}`.slice(0, 2000),
        stack: reason?.stack || null,
      });
    } catch { /* never throw */ }
  });

  // Track last user action (clicks on buttons/links) for context
  document.addEventListener("click", (e) => {
    try {
      const el = (e.target as HTMLElement)?.closest?.("button, a, [role='button']");
      if (!el) return;
      const label = (el.getAttribute("aria-label") || el.textContent || "").trim().slice(0, 60);
      if (label) setLastUserAction(`clicked "${label}"`);
    } catch { /* never throw */ }
  }, { capture: true, passive: true });
}
